import React, { useEffect, useState } from 'react';
import { ChevronRightIcon } from 'lucide-react';
import { LicenseDashboard } from './license/LicenseDashboard';
import { ModuleLicensing } from './license/ModuleLicensing';
import { VolumePlans } from './license/VolumePlans';
import { UsageTracking } from './license/UsageTracking';
import { OccasionalPasses } from './license/OccasionalPasses';
import { ExternalServices } from './license/ExternalServices';
export function LicenseManager({
  locale = 'en-US'
}) {
  const [activeTab, setActiveTab] = useState('overview');
  const [showModuleForm, setShowModuleForm] = useState(false);
  const [selectedModule, setSelectedModule] = useState(null);
  // Default modules data - UPDATED with consistent IDs
  const defaultModules = [{
    id: 'accounts-payable',
    name: 'Accounts Payable',
    licenseType: 'Per-User',
    startDate: '2023-01-01',
    endDate: '2023-12-31',
    renewalMode: 'Auto',
    status: 'Active',
    maxUsers: 50,
    currentUsers: 32,
    cost: 5000,
    currency: 'USD',
    entities: ['Global'],
    sites: ['All Sites']
  }, {
    id: 'vendor-onboarding',
    name: 'Vendor Onboarding',
    licenseType: 'Per-User',
    startDate: '2023-02-15',
    endDate: '2023-11-15',
    renewalMode: 'Manual',
    status: 'Active',
    maxUsers: 25,
    currentUsers: 18,
    cost: 2500,
    currency: 'USD',
    entities: ['Global Technologies Inc.'],
    sites: ['New York HQ', 'San Francisco Office']
  }, {
    id: 'purchase-order',
    name: 'Purchase Order',
    licenseType: 'Volume-Based',
    startDate: '2023-03-01',
    endDate: '2024-02-29',
    renewalMode: 'Auto',
    status: 'Active',
    volumeMetric: 'Invoices',
    maxVolume: 10000,
    currentVolume: 5230,
    cost: 7500,
    currency: 'USD',
    entities: ['Global Technologies Inc.', 'European Operations Ltd'],
    sites: ['All Sites']
  }, {
    id: 'contract-management',
    name: 'Contract Management',
    licenseType: 'Per-User',
    startDate: '2023-01-01',
    endDate: '2023-10-31',
    renewalMode: 'Manual',
    status: 'Expiring Soon',
    maxUsers: 15,
    currentUsers: 12,
    cost: 3000,
    currency: 'USD',
    entities: ['Global Technologies Inc.'],
    sites: ['New York HQ']
  }, {
    id: 'vendor-portal',
    name: 'Vendor Portal',
    licenseType: 'Flat-Fee',
    startDate: '2023-05-01',
    endDate: '2024-04-30',
    renewalMode: 'Auto',
    status: 'Active',
    maxSuppliers: 500,
    currentSuppliers: 342,
    cost: 10000,
    currency: 'USD',
    entities: ['Global'],
    sites: ['All Sites']
  }, {
    id: 'expense-management',
    name: 'Expense Management',
    licenseType: 'Per-User',
    startDate: '2023-01-01',
    endDate: '2023-09-15',
    renewalMode: 'Manual',
    status: 'Expired',
    maxUsers: 10,
    currentUsers: 8,
    cost: 5000,
    currency: 'USD',
    entities: ['Global Technologies Inc.'],
    sites: ['New York HQ']
  }];
  // Initialize modules from localStorage or use defaults
  const [modules, setModules] = useState(() => {
    const savedModules = localStorage.getItem('licenseManagerModules');
    if (savedModules) {
      try {
        return JSON.parse(savedModules);
      } catch (e) {
        console.error('Failed to parse saved modules:', e);
        return defaultModules;
      }
    }
    return defaultModules;
  });
  // Save to localStorage whenever modules change
  useEffect(() => {
    localStorage.setItem('licenseManagerModules', JSON.stringify(modules));
  }, [modules]);
  // Handle module updates from ModuleLicensing
  const handleModuleUpdate = updatedModules => {
    console.log('Updating modules:', updatedModules);
    setModules(updatedModules);
  };
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showPlanForm, setShowPlanForm] = useState(false);
  const [showPassForm, setShowPassForm] = useState(false);
  // Breadcrumb navigation state
  const [breadcrumbs, setBreadcrumbs] = useState([{
    label: 'License Manager',
    tab: 'dashboard'
  }]);
  // Update breadcrumbs when active tab changes
  const handleTabChange = (tab, item = null) => {
    setActiveTab(tab);
    if (tab === 'module-detail' && item) {
      setSelectedModule(item);
      setBreadcrumbs([{
        label: 'License Manager',
        tab: 'dashboard'
      }, {
        label: 'Modules & Licensing',
        tab: 'modules'
      }, {
        label: item.name,
        tab: 'module-detail'
      }]);
    } else if (tab === 'plan-detail' && item) {
      setSelectedPlan(item);
      setBreadcrumbs([{
        label: 'License Manager',
        tab: 'dashboard'
      }, {
        label: 'Volume Plans',
        tab: 'volume-plans'
      }, {
        label: item.name,
        tab: 'plan-detail'
      }]);
    } else if (tab === 'modules') {
      setBreadcrumbs([{
        label: 'License Manager',
        tab: 'dashboard'
      }, {
        label: 'Modules & Licensing',
        tab: 'modules'
      }]);
    } else if (tab === 'volume-plans') {
      setBreadcrumbs([{
        label: 'License Manager',
        tab: 'dashboard'
      }, {
        label: 'Volume Plans',
        tab: 'volume-plans'
      }]);
    } else if (tab === 'usage') {
      setBreadcrumbs([{
        label: 'License Manager',
        tab: 'dashboard'
      }, {
        label: 'Usage Tracking',
        tab: 'usage'
      }]);
    } else if (tab === 'passes') {
      setBreadcrumbs([{
        label: 'License Manager',
        tab: 'dashboard'
      }, {
        label: 'Occasional Passes',
        tab: 'passes'
      }]);
    } else if (tab === 'external') {
      setBreadcrumbs([{
        label: 'License Manager',
        tab: 'dashboard'
      }, {
        label: 'External Services',
        tab: 'external'
      }]);
    } else {
      setBreadcrumbs([{
        label: 'License Manager',
        tab: 'dashboard'
      }]);
    }
  };
  // Handle breadcrumb navigation
  const handleBreadcrumbClick = tab => {
    handleTabChange(tab);
  };
  // Module license form handlers
  const handleAddModule = () => {
    setSelectedModule(null);
    setShowModuleForm(true);
  };
  const handleEditModule = module => {
    setSelectedModule(module);
    setShowModuleForm(true);
  };
  const handleViewModule = module => {
    setSelectedModule(module);
    handleTabChange('module-detail', module);
  };
  // Volume plan form handlers
  const handleAddPlan = () => {
    setSelectedPlan(null);
    setShowPlanForm(true);
  };
  const handleEditPlan = plan => {
    setSelectedPlan(plan);
    setShowPlanForm(true);
  };
  // Occasional passes form handler
  const handleAddPasses = () => {
    setShowPassForm(true);
  };
  return <div className="space-y-6" data-id="element-16107">
      <div className="mb-8" data-id="element-16108">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-16109">License Manager</h2>
        <p className="mt-1 text-sm text-gray-500" data-id="element-16110">
          Manage module licensing, volume usage, and occasional passes
        </p>
      </div>
      {/* Breadcrumbs */}
      <nav className="flex mb-4" aria-label="Breadcrumb" data-id="element-16111">
        <ol className="inline-flex items-center space-x-1 md:space-x-3" data-id="element-16112">
          {breadcrumbs.map((crumb, index) => <li key={index} className="inline-flex items-center" data-id="element-16113">
              {index > 0 && <ChevronRightIcon className="w-4 h-4 text-gray-400 mx-1" data-id="element-16114" />}
              {index < breadcrumbs.length - 1 ? <button onClick={() => handleBreadcrumbClick(crumb.tab)} className="inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-800" data-id="element-16115">
                  {crumb.label}
                </button> : <span className="text-sm font-medium text-gray-500" data-id="element-16116">
                  {crumb.label}
                </span>}
            </li>)}
        </ol>
      </nav>
      {/* Tabs */}
      <div className="border-b border-gray-200" data-id="element-16117">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs" data-id="element-16118">
          <button className={`${activeTab === 'dashboard' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} onClick={() => handleTabChange('dashboard')} data-id="element-16119">
            Dashboard
          </button>
          <button className={`${activeTab === 'modules' || activeTab === 'module-detail' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} onClick={() => handleTabChange('modules')} data-id="element-16120">
            Modules & Licensing
          </button>
          <button className={`${activeTab === 'volume-plans' || activeTab === 'plan-detail' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} onClick={() => handleTabChange('volume-plans')} data-id="element-16121">
            Volume Plans
          </button>
          <button className={`${activeTab === 'usage' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} onClick={() => handleTabChange('usage')} data-id="element-16122">
            Usage Tracking
          </button>
          <button className={`${activeTab === 'passes' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} onClick={() => handleTabChange('passes')} data-id="element-16123">
            Occasional Passes
          </button>
          <button className={`${activeTab === 'external' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} onClick={() => handleTabChange('external')} data-id="element-16124">
            External Services
          </button>
        </nav>
      </div>
      {/* Tab Content */}
      <div className="mt-6" data-id="element-16125">
        {activeTab === 'dashboard' && <LicenseDashboard locale={locale} onNavigate={handleTabChange} data-id="element-16126" />}
        {activeTab === 'modules' && <ModuleLicensing locale={locale} onAddModule={handleAddModule} onEditModule={handleEditModule} onViewModule={handleViewModule} showModuleForm={showModuleForm} setShowModuleForm={setShowModuleForm} selectedModule={selectedModule} onModuleUpdate={handleModuleUpdate} modules={modules} data-id="element-16127" />}
        {activeTab === 'volume-plans' && <VolumePlans locale={locale} onAddPlan={handleAddPlan} onEditPlan={handleEditPlan} onViewPlan={plan => handleTabChange('plan-detail', plan)} showPlanForm={showPlanForm} setShowPlanForm={setShowPlanForm} selectedPlan={selectedPlan} data-id="element-16128" />}
        {activeTab === 'usage' && <UsageTracking locale={locale} data-id="element-16129" />}
        {activeTab === 'passes' && <OccasionalPasses locale={locale} onAddPasses={handleAddPasses} showPassForm={showPassForm} setShowPassForm={setShowPassForm} data-id="element-16130" />}
        {activeTab === 'external' && <ExternalServices locale={locale} data-id="element-16131" />}
      </div>
    </div>;
}